import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  AvailableNodesQuery,
  useMyCorankLazyQuery,
  useMyCorankQuery,
  useNodeByIdLazyQuery,
} from '../generated/graphql'
import { useAuthContext } from './auth-context'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { Unpacked } from '../utils/types'
import { MdNode } from '../screens/ListNodes/types'
import Toast from 'react-native-root-toast'
import { MessageType } from '../utils/message-type'
import { useNavigation } from '@react-navigation/native'
import { AppNavigation } from '../navigation/types'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react-native'
import { ERROR_CODES } from '../utils/error-utils'
import { hasApolloErrorCode } from '../utils/error-utils'

interface CurrentNodeProviderProps {
  children: ReactNode
}

interface CurrentNodeProviderShape {
  currentNode?: NdNode
  setCurrentNode: (nodeId: string) => void
  loading: boolean
  refreshCorank: (nodeId: string) => void
  userCorank: number | null
}

const CURRENT_NODE_KEY = 'currentNode'

const CurrentNodeContext = createContext<CurrentNodeProviderShape>({
  currentNode: undefined,
  setCurrentNode: () => {},
  loading: false,
  refreshCorank: () => {},
  userCorank: null,
})

type NdNode = Unpacked<AvailableNodesQuery['availableNodes']>

export function CurrentNodeProvider(props: CurrentNodeProviderProps) {
  const { userId, isAnonymous, openLoginModal } = useAuthContext()
  const [currentNode, _setCurrentNode] = useState<MdNode>()
  const [doLoadNode] = useNodeByIdLazyQuery()
  const [loading, setLoading] = useState(true)
  const [userCorank, setUserCorank] = useState<number | null>(null)
  const [doLoadCorank] = useMyCorankLazyQuery()
  const navigation = useNavigation<AppNavigation>()
  const { t } = useTranslation('nodes')
  const setCurrentNode = useCallback(
    async (nodeId: string) => {
      if (!nodeId) {
        // AsyncStorage.removeItem(`${CURRENT_NODE_KEY}:${userId}`)
        return
      }
      try {
        setLoading(true)
        const { data, error } = await doLoadNode({
          variables: { id: nodeId },
          // There is an issue with the cache: You are in a private node, logout and reload, you will get the security error. After login,
          // the call is cached not using the token (after you logout) and keep throwing security error.
          // With this solution may send 1 extra error while you are logging but then is working fine.
          // fetchPolicy: 'cache-and-network',
        })

        if (data) {
          _setCurrentNode(data['nodeById'])
          AsyncStorage.setItem(`${CURRENT_NODE_KEY}:${userId}`, nodeId)
          return
        }

        if (error) {
          if (
            hasApolloErrorCode(error, [
              ERROR_CODES.FORBIDDEN,
              ERROR_CODES.METHOD_NOT_ALLOWED,
            ])
          ) {
            isAnonymous
              ? openLoginModal(navigation)
              : Toast.show(t('private-node-login-required'), {
                  position: Toast.positions.CENTER,
                })
            return
          }

          Toast.show(error.message, MessageType.error)
        }
      } catch (e) {
        console.error(e)
        Sentry.captureException(e)

        Toast.show((e as Error).message, MessageType.error)
      } finally {
        setLoading(false)
      }
    },
    [doLoadNode, userId, navigation, isAnonymous]
  )

  useEffect(() => {
    setCurrentNode(currentNode?.id || '')
  }, [userId])

  const refreshCorank = useCallback(
    async (nodeId: string) => {
      if (!nodeId) {
        setUserCorank(null)
        return
      }

      if (isAnonymous) {
        setUserCorank(null)
        return
      }
      const result = await doLoadCorank({
        variables: { nodeId },
        fetchPolicy: 'network-only', // always hit server
      })

      setUserCorank(
        result.data?.myCorank !== undefined ? result.data.myCorank : null
      )
    },
    [doLoadCorank, isAnonymous]
  )

  useEffect(() => {
    if (currentNode?.id) {
      refreshCorank(currentNode?.id)
    }
  }, [refreshCorank, currentNode?.id, userId, isAnonymous])

  useEffect(() => {
    const loadFromStorage = async () => {
      const nodeId = await AsyncStorage.getItem(`${CURRENT_NODE_KEY}:${userId}`)
      if (nodeId) {
        setCurrentNode(nodeId)
      }
    }
    loadFromStorage()
  }, [userId])

  return (
    <CurrentNodeContext.Provider
      value={
        {
          currentNode,
          setCurrentNode,
          loading,
          userCorank,
          refreshCorank,
        } as CurrentNodeProviderShape
      }
      {...props}
    />
  )
}

export const useNode = () => {
  const context = useContext(CurrentNodeContext)

  return context
}
